// AC-956 / S4.2 — a small allowlist HTML sanitizer for the layout `html` element.
//
// The `html` element is an escape hatch for markup the typed elements can't express. We never render
// author-supplied HTML raw: this parses it with DOMParser (inert — parsing does not run scripts or
// load resources) and then removes dangerous elements outright, strips every on* event-handler
// attribute, and neutralizes javascript:/vbscript:/data: URLs. Scripts therefore never execute.
// This is intentionally conservative for header/footer snippets, not a general-purpose HTML5
// sanitizer — when in doubt it drops rather than keeps.

// Elements removed entirely, subtree and all. Anything that can execute script, load a remote
// document, submit data, or smuggle style/script counts.
const DANGEROUS_TAGS = new Set([
    'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'BASE', 'FORM', 'INPUT',
    'TEXTAREA', 'SELECT', 'BUTTON', 'SVG', 'MATH', 'TITLE', 'NOSCRIPT', 'TEMPLATE', 'FRAME', 'FRAMESET',
]);

// Attributes that hold a URL — checked for dangerous schemes.
const URL_ATTRS = new Set(['href', 'src', 'xlink:href', 'action', 'formaction', 'background', 'poster', 'srcset']);
const DANGEROUS_SCHEME = /^\s*(javascript|vbscript|data):/i;

function sanitizeElement(el: Element): void {
    // Snapshot children — we mutate the list as we go.
    Array.from(el.children).forEach((child) => {
        if (DANGEROUS_TAGS.has(child.tagName)) {
            child.remove();
            return;
        }
        Array.from(child.attributes).forEach((attr) => {
            const name = attr.name.toLowerCase();
            const value = attr.value;
            if (name.startsWith('on')) {
                child.removeAttribute(attr.name);
            } else if (URL_ATTRS.has(name) && DANGEROUS_SCHEME.test(value)) {
                // Allow inline data:image/* on an <img src> (common, harmless); drop everything else.
                const isInlineImage = child.tagName === 'IMG' && name === 'src' && /^\s*data:image\//i.test(value);
                if (!isInlineImage) child.removeAttribute(attr.name);
            } else if (name === 'style' && /(javascript:|expression\s*\(|url\s*\(\s*['"]?\s*(javascript|vbscript):)/i.test(value)) {
                child.removeAttribute(attr.name);
            }
        });
        sanitizeElement(child);
    });
}

export function sanitizeHtml(dirty: unknown): string {
    if (!dirty || typeof dirty !== 'string') return '';
    const doc = new DOMParser().parseFromString(dirty, 'text/html');
    sanitizeElement(doc.body);
    return doc.body.innerHTML;
}
